import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Link } from '@tanstack/react-router';
import { Icon } from './icons';

export const ACCOUNT_MENU_ID = 'account-menu';

export interface AccountMenuProps {
  displayName: string;
  email: string;
  hasProfile: boolean;
  anchorRef: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onLogout: () => void;
}

const VIEWPORT_MARGIN = 12;
const ANCHOR_GAP = 8;
const FOCUSABLE_SELECTOR =
  'a[href], button:not(:disabled), input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

export function AccountMenu({
  displayName,
  email,
  hasProfile,
  anchorRef,
  onClose,
  onLogout,
}: AccountMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({
    top: 0,
    right: VIEWPORT_MARGIN,
    width: 300,
    maxHeight: 0,
  });
  const [positioned, setPositioned] = useState(false);

  useLayoutEffect(() => {
    const menu = menuRef.current;
    const anchor = anchorRef.current;
    if (!menu || !anchor) return;

    const place = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const width = Math.max(0, Math.min(300, viewportWidth - VIEWPORT_MARGIN * 2));
      const maxHeight = Math.max(0, viewportHeight - VIEWPORT_MARGIN * 2);
      const anchorRect = anchor.getBoundingClientRect();
      const menuHeight = Math.min(menu.scrollHeight, maxHeight);
      const right = Math.max(
        VIEWPORT_MARGIN,
        Math.min(viewportWidth - anchorRect.right, viewportWidth - width - VIEWPORT_MARGIN),
      );
      const below = anchorRect.bottom + ANCHOR_GAP;
      const above = anchorRect.top - ANCHOR_GAP - menuHeight;
      const preferredTop =
        below + menuHeight > viewportHeight - VIEWPORT_MARGIN && above >= VIEWPORT_MARGIN
          ? above
          : below;
      const top = Math.max(
        VIEWPORT_MARGIN,
        Math.min(preferredTop, viewportHeight - menuHeight - VIEWPORT_MARGIN),
      );
      setPosition((previous) =>
        previous.top === top &&
        previous.right === right &&
        previous.width === width &&
        previous.maxHeight === maxHeight
          ? previous
          : { top, right, width, maxHeight },
      );
      setPositioned(true);
    };

    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, { capture: true, passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
    observer?.observe(menu);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      observer?.disconnect();
    };
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (positioned)
      menuRef.current
        ?.querySelector<HTMLElement>('[role="menuitem"]')
        ?.focus({ preventScroll: true });
  }, [positioned]);

  useEffect(() => {
    const outside = (event: PointerEvent) => {
      if (!(event.target instanceof Node)) return;
      if (menuRef.current?.contains(event.target) || anchorRef.current?.contains(event.target))
        return;
      onClose();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
      anchorRef.current?.focus({ preventScroll: true });
    };
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('keydown', escape);
    };
  }, [anchorRef, onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      id={ACCOUNT_MENU_ID}
      ref={menuRef}
      className="account-menu"
      role="menu"
      aria-label="Меню учётной записи"
      aria-orientation="vertical"
      style={{
        position: 'fixed',
        ...position,
        visibility: positioned ? 'visible' : 'hidden',
        overflowY: 'auto',
      }}
      onKeyDown={(event) => {
        const menu = menuRef.current;
        if (!menu) return;
        if (event.key === ' ' && event.target instanceof HTMLAnchorElement) {
          event.preventDefault();
          event.target.click();
          return;
        }
        if (event.key === 'Tab') {
          event.preventDefault();
          const controls = Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
            .filter((control) => !menu.contains(control))
            .filter((control) => !control.closest('[hidden], [inert]'))
            .filter((control) => control.tabIndex >= 0);
          const anchor = anchorRef.current;
          const index = anchor ? controls.indexOf(anchor) : -1;
          const next = controls[index + (event.shiftKey ? -1 : 1)] || anchor;
          onClose();
          next?.focus({ preventScroll: true });
          return;
        }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const items = Array.from(menu.querySelectorAll<HTMLElement>('[role="menuitem"]'));
        if (!items.length) return;
        const current = items.indexOf(document.activeElement as HTMLElement);
        const next =
          event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? items.length - 1
              : (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
        items[next]?.focus({ preventScroll: true });
      }}
    >
      <div className="account-menu-header">
        <span className="account-menu-name">{displayName || email}</span>
        {email && email !== (displayName || email) && (
          <span className="account-menu-email">{email}</span>
        )}
      </div>
      <div className="account-menu-items" role="none">
        {hasProfile && (
          <Link
            to="/my-profile"
            role="menuitem"
            tabIndex={-1}
            className="account-menu-item"
            onClick={onClose}
          >
            <Icon name="Profile" size={18} />
            <span>Мой профиль</span>
          </Link>
        )}
        <Link
          to="/account/appearance"
          role="menuitem"
          tabIndex={-1}
          className="account-menu-item"
          onClick={onClose}
        >
          <Icon name="Controls" size={18} />
          <span>Настройки</span>
        </Link>
        <div className="account-menu-separator" role="separator" />
        <button
          type="button"
          role="menuitem"
          tabIndex={-1}
          className="account-menu-item account-menu-logout"
          onClick={() => {
            onClose();
            onLogout();
          }}
        >
          <svg
            className="account-menu-logout-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 4H4v16h5M9 12h11m-4-4 4 4-4 4" />
          </svg>
          <span>Выйти</span>
        </button>
      </div>
    </div>,
    document.body,
  );
}
